/**
 * OverlapCard — Displays a single overlap flag in OverlapReviewDashboard.
 * Blueprint: F-05 (response shape) + F-06 (review actions).
 *
 * Confidence → color:   confirmed → green  |  probable → amber  |  unverified → gray
 * Rule codes reference: ACP-METH-OVERLAP-2026-002 (stored in rule_applied field)
 *
 * TODO: Fetch evidence_on_file links from S3 pre-signed URLs.
 * TODO: Accept sets reviewed_by_user_id + status stays CONFIRMED.
 * TODO: Reject sets status=REJECTED + removes tag from overlap_tags JSONB.
 */
import React from 'react';
import { GitMerge, CheckCircle, XCircle, FileText } from 'lucide-react';
import type { OverlapFlag, ConfidenceScore } from '@/types/cmm/claimant.types';

const CONFIDENCE_STYLES: Record<ConfidenceScore, { label: string; cls: string }> = {
  confirmed:  { label: 'Confirmed',  cls: 'bg-green-100 text-green-700 border-green-300' },
  probable:   { label: 'Probable',   cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  unverified: { label: 'Unverified', cls: 'bg-gray-100  text-gray-600  border-gray-300'  },
};

interface OverlapCardProps {
  overlap:    OverlapFlag;
  onAccept?:  (id: string) => void;
  onReject?:  (id: string) => void;
  showActions?: boolean;
}

export const OverlapCard: React.FC<OverlapCardProps> = ({
  overlap, onAccept, onReject, showActions = true,
}) => {
  const conf = CONFIDENCE_STYLES[overlap.confidenceScore];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <GitMerge className="w-4 h-4 text-blue-500" />
          <p className="text-sm font-semibold text-gray-900">{overlap.litigationName}</p>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${conf.cls}`}>
          {conf.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
        <div><span className="font-medium text-gray-700">Match Type:</span> {overlap.matchType}</div>
        <div><span className="font-medium text-gray-700">Rule:</span> <code className="bg-gray-100 px-1 rounded">{overlap.ruleApplied}</code></div>
      </div>

      {overlap.evidenceOnFile.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">Evidence on file:</p>
          <ul className="space-y-0.5">
            {overlap.evidenceOnFile.map((ev, i) => (
              <li key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                <FileText className="w-3 h-3 text-green-500" /> {ev}
              </li>
            ))}
          </ul>
        </div>
      )}

      {overlap.evidenceGaps.length > 0 && (
        <div>
          <p className="text-xs font-medium text-red-600 mb-1">Evidence gaps:</p>
          <ul className="list-disc list-inside space-y-0.5">
            {overlap.evidenceGaps.map((gap, i) => (
              <li key={i} className="text-xs text-red-500">{gap}</li>
            ))}
          </ul>
        </div>
      )}

      {showActions && overlap.status === 'CONFIRMED' && !overlap.reviewedByUserId && (
        <div className="flex gap-2 pt-2 border-t border-gray-100">
          <button onClick={() => onAccept?.(overlap.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100">
            <CheckCircle className="w-3.5 h-3.5" /> Accept
          </button>
          <button onClick={() => onReject?.(overlap.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100">
            <XCircle className="w-3.5 h-3.5" /> Reject
          </button>
        </div>
      )}
    </div>
  );
};

export default OverlapCard;
